import { z } from "zod";
import { EntityProfileType } from "@common/types/entity";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { WhoPaysOption } from "@common/types/filters/clients";
import { OrganizationServicesOffered } from "@common/types/organization";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { ENGAGEMENT_TEAM_ROLES } from "@common/types/taskTemplateAssigneeRole";
import { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import { CloseApplicationType } from "@common/types/closeApplicationType";
import { CloseHealth, CloseHealthReason } from "@common/types/closeHealth";
import { CloseProgressStatus } from "@common/types/closeProgress";
import { CloseStatus } from "@common/types/closeStatus";
import { clientOrgBillingTransferRequestSchema } from "@common/schemas/client.schema";
import { zQueryBoolean } from "@common/schemas/query-boolean.schema";
import { featureDisabledResponseSchema } from "@common/schemas/feature-flag-response.schema";

// ────────────────────────────────────────────────────────────
// Shared member shape (aligns with MemberSchema in client.schema.ts)
// ────────────────────────────────────────────────────────────
export const closeOverviewMemberSchema = z.object({
    userId: z.number(),
    role: z.nativeEnum(UserWorkspaceRole),
    email: z.string(),
    iconColor: z.string(),
    avatar: z.string().nullable(),
    preferredName: z.string().nullable(),
    organizationId: z.string().nullable(),
});
export type CloseOverviewMember = z.infer<typeof closeOverviewMemberSchema>;

// ────────────────────────────────────────────────────────────
// Close-level row
// ────────────────────────────────────────────────────────────
/** Per-assignee estimate breakdown for a close (from live tasks). */
export const closeOverviewEstimatedTimeByMemberSchema = z.object({
    /** Null when tasks with estimates have no assignee. */
    userId: z.number().int().nullable(),
    preferredName: z.string().nullable(),
    email: z.string().nullable(),
    remainingMinutes: z.number().int().nonnegative(),
    doneMinutes: z.number().int().nonnegative(),
});
export type CloseOverviewEstimatedTimeByMember = z.infer<
    typeof closeOverviewEstimatedTimeByMemberSchema
>;

/**
 * A role's open tasks that a role change reassigns (template task has the
 * role and no fixed assignee). `assigneeIds` are their distinct assignees;
 * `hasOpenTasks` is true when at least one exists, assigned or not.
 */
export const closeOverviewOpenRoleSchema = z.object({
    assigneeIds: z.array(z.number().int()),
    hasOpenTasks: z.boolean(),
});
export type CloseOverviewOpenRole = z.infer<typeof closeOverviewOpenRoleSchema>;

export const closeOverviewOpenRolesSchema = z.object({
    preparer: closeOverviewOpenRoleSchema,
    reviewer: closeOverviewOpenRoleSchema,
    manager: closeOverviewOpenRoleSchema,
});
export type CloseOverviewOpenRoles = z.infer<
    typeof closeOverviewOpenRolesSchema
>;

export const closeOverviewCloseRowSchema = z.object({
    id: z.string(),
    rowType: z.literal("close"),
    name: z.string(),
    startDate: z.string().date().nullable(),
    endDate: z.string().date().nullable(),
    workStartDate: z.string().date().nullable(),
    workDeliveryDate: z.string().date().nullable(),
    closeStageId: z.string().uuid().nullable(),
    closeStageName: z.string().nullable(),
    closeTemplateId: z.string().uuid(),
    // Set when this close was created by the automatic recurrence occurrence
    // apply job (matches ServiceTemplateRecurrenceEntity.id, a.k.a. scheduleId).
    // Null for closes applied manually via the apply-template flow.
    recurrenceId: z.string().uuid().nullable(),
    closeStageSetAt: z.string().datetime().nullable(),
    isFinished: z.boolean(),
    cancelledAt: z.string().datetime().nullable(),
    terminatedTasksCount: z.number().int().nonnegative(),
    totalTaskCount: z.number().int().nonnegative(),
    /**
     * Sum of `estimatedTime` (minutes) on non-cancelled, non-done tasks.
     * Cancelled tasks are excluded; null estimates contribute 0.
     */
    estimatedTimeRemaining: z.number().int().nonnegative(),
    /**
     * Sum of `estimatedTime` (minutes) on tasks with status `done`.
     * Cancelled tasks are excluded; null estimates contribute 0.
     */
    estimatedTimeDone: z.number().int().nonnegative(),
    /** Per-assignee remaining/done minutes (unassigned grouped under userId null). */
    estimatedTimeByMember: z.array(closeOverviewEstimatedTimeByMemberSchema),
    service: z.object({ id: z.string(), name: z.string() }).nullable(),
    preparers: z.array(closeOverviewMemberSchema),
    reviewers: z.array(closeOverviewMemberSchema),
    managers: z.array(closeOverviewMemberSchema),
    openRoles: closeOverviewOpenRolesSchema,
    createdByUserId: z.number().int().nullable(),
    createdAt: z.string().datetime(),
    applicationType: z.nativeEnum(CloseApplicationType),
    health: z.nativeEnum(CloseHealth),
    healthReason: z.nativeEnum(CloseHealthReason).nullable(),
    isAgentRunning: z.boolean(),
});
export type CloseOverviewCloseRow = z.infer<typeof closeOverviewCloseRowSchema>;

// ────────────────────────────────────────────────────────────
// Entity-level row
// ────────────────────────────────────────────────────────────
export const closeOverviewEntityRowSchema = z.object({
    id: z.string(),
    rowType: z.literal("entity"),
    name: z.string().nullable(),
    iconColor: z.string(),
    logo: z.string().nullable(),
    isBusiness: z.boolean(),
    legalType: z.nativeEnum(EntityProfileType).nullable(),
    note: z.string().nullable().optional(),
    services: z
        .array(z.nativeEnum(OrganizationServicesOffered))
        .nullable()
        .optional(),
    teamMembers: z.array(closeOverviewMemberSchema),
    closeResponsibleMembers: z.array(closeOverviewMemberSchema),
    children: z.array(closeOverviewCloseRowSchema),
    bookkeepingStartDate: z.string().nullable(),
});
export type CloseOverviewEntityRow = z.infer<
    typeof closeOverviewEntityRowSchema
>;

// ────────────────────────────────────────────────────────────
// Slim entity shape used in the entities column
// ────────────────────────────────────────────────────────────
export const closeOverviewEntityIconSchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    iconColor: z.string(),
    logo: z.string().nullable(),
    isBusiness: z.boolean(),
    workspaceId: z.string(),
});
export type CloseOverviewEntityIcon = z.infer<
    typeof closeOverviewEntityIconSchema
>;

// ────────────────────────────────────────────────────────────
// Workspace-level row
// ────────────────────────────────────────────────────────────
export const closeOverviewWorkspaceRowSchema = z.object({
    id: z.string(),
    rowType: z.literal("workspace"),
    name: z.string(),
    plan: z.nativeEnum(FLAT_RATE_PLANS).nullable(),
    isOrganizationBillingOwner: z.boolean(),
    isFirmWorkspace: z.boolean(),
    hasUsersToTransferBillingTo: z.boolean(),
    orgBillingTransferRequest: clientOrgBillingTransferRequestSchema.nullable(),
    note: z.string().nullable().optional(),
    bookkeepingStartDate: z.string().nullable(),
    createdAt: z.string().datetime(),
    services: z
        .array(z.nativeEnum(OrganizationServicesOffered))
        .nullable()
        .optional(),
    teamMembers: z.array(closeOverviewMemberSchema),
    accessMembers: z.array(closeOverviewMemberSchema),
    entities: z.array(closeOverviewEntityIconSchema),
    children: z.array(closeOverviewEntityRowSchema),
});
export type CloseOverviewWorkspaceRow = z.infer<
    typeof closeOverviewWorkspaceRowSchema
>;

// ────────────────────────────────────────────────────────────
// Query filters
// ────────────────────────────────────────────────────────────
export const clientsCloseOverviewFiltersSchema = z.object({
    search: z.string().optional(),
    clientIds: z.array(z.string().uuid()).optional(),
    closeTemplateIds: z.array(z.string().uuid()).optional(),
    serviceIds: z.array(z.string().uuid()).optional(),
    noTier: z.coerce.boolean().optional(),
    closeStageIds: z.array(z.string().uuid()).optional(),
    closeStatuses: z.array(z.nativeEnum(CloseStatus)).optional(),
    closeDateStart: z.string().date().optional(),
    closeDateEnd: z.string().date().optional(),
    bookkeepingStartDateStart: z.string().date().optional(),
    bookkeepingStartDateEnd: z.string().date().optional(),
    workStartDateStart: z.string().date().optional(),
    workStartDateEnd: z.string().date().optional(),
    workDeliveryDateStart: z.string().date().optional(),
    workDeliveryDateEnd: z.string().date().optional(),
    /** `false` matches closes with no work end date. */
    hasWorkDeliveryDate: zQueryBoolean().pipe(z.literal(false)).optional(),
    daysInStageOperator: z.enum(["gt", "lt", "eq", "between"]).optional(),
    daysInStageMin: z.coerce.number().int().nonnegative().optional(),
    daysInStageMax: z.coerce.number().int().nonnegative().optional(),
    teamMemberIds: z.array(z.coerce.number()).optional(),
    legalTypes: z.array(z.nativeEnum(EntityProfileType)).optional(),
    plans: z.array(z.nativeEnum(FLAT_RATE_PLANS)).optional(),
    whoPays: z.array(z.nativeEnum(WhoPaysOption)).optional(),
    services: z.array(z.nativeEnum(OrganizationServicesOffered)).optional(),
    engagementTeamRoles: z.array(z.enum(ENGAGEMENT_TEAM_ROLES)).optional(),
    engagementTeamRoleOperator: z.enum(["any", "every"]).optional(),
    engagementTeamMemberIds: z.array(z.coerce.number().int()).optional(),
    engagementTeamMemberOperator: z.enum(["any", "every"]).optional(),
    applicationTypes: z.array(z.nativeEnum(CloseApplicationType)).optional(),
    progressStatuses: z.array(z.nativeEnum(CloseProgressStatus)).optional(),
    healthStatuses: z.array(z.nativeEnum(CloseHealth)).optional(),
    /**
     * When true, matches closes with unread task messages for the requesting
     * user. When false, matches closes with none.
     */
    hasUnread: zQueryBoolean().optional(),
});
export type ClientsCloseOverviewFilters = z.infer<
    typeof clientsCloseOverviewFiltersSchema
>;

// ────────────────────────────────────────────────────────────
// Shared refinement helpers
// ────────────────────────────────────────────────────────────
interface RefinementInput {
    filters?: ClientsCloseOverviewFilters;
}

function isDateRangeValid(
    startField: keyof ClientsCloseOverviewFilters,
    endField: keyof ClientsCloseOverviewFilters,
): [
    (v: RefinementInput) => boolean,
    { message: string; path: Array<string | number> },
] {
    return [
        (v) => {
            const start = v.filters?.[startField];
            const end = v.filters?.[endField];
            return !start || !end || end >= start;
        },
        {
            message: `${endField} must be >= ${startField}`,
            path: ["filters", endField],
        },
    ];
}

function isDaysInStageValid(): [
    (v: RefinementInput) => boolean,
    { message: string; path: string[] },
] {
    return [
        (v) => {
            const f = v.filters;
            if (!f?.daysInStageOperator) {
                return true;
            }
            if (f.daysInStageMin === undefined) {
                return false;
            }
            if (
                f.daysInStageOperator === "between" &&
                f.daysInStageMax === undefined
            ) {
                return false;
            }
            if (
                f.daysInStageOperator === "between" &&
                f.daysInStageMax !== undefined &&
                f.daysInStageMin > f.daysInStageMax
            ) {
                return false;
            }
            return true;
        },
        {
            message:
                "daysInStageMin is required when operator is set; daysInStageMax is required when operator is 'between' and must be >= daysInStageMin",
            path: ["filters", "daysInStageMin"],
        },
    ];
}

const filteredQueryBaseSchema = z.object({
    organizationId: z.string().uuid(),
    filters: clientsCloseOverviewFiltersSchema.optional(),
});

/** Adds the filter range checks every filtered close overview query shares. */
function refineFilterQuery<Shape extends typeof filteredQueryBaseSchema.shape>(
    schema: z.ZodObject<Shape>,
) {
    return schema
        .refine(...isDateRangeValid("closeDateStart", "closeDateEnd"))
        .refine(
            ...isDateRangeValid(
                "bookkeepingStartDateStart",
                "bookkeepingStartDateEnd",
            ),
        )
        .refine(...isDateRangeValid("workStartDateStart", "workStartDateEnd"))
        .refine(
            ...isDateRangeValid("workDeliveryDateStart", "workDeliveryDateEnd"),
        )
        .refine(...isDaysInStageValid());
}

// ────────────────────────────────────────────────────────────
// Full query schema
// ────────────────────────────────────────────────────────────
export const clientsCloseOverviewQuerySchema = refineFilterQuery(
    filteredQueryBaseSchema.extend({
        page: z.coerce.number().int().min(1).optional(),
        limit: z.coerce.number().int().min(1).max(100).optional(),
        sort: z.nativeEnum(ClientsWorkOverviewSortValue).optional(),
    }),
);
export type ClientsCloseOverviewQuery = z.infer<
    typeof clientsCloseOverviewQuerySchema
>;

// ────────────────────────────────────────────────────────────
// Response schema
// ────────────────────────────────────────────────────────────
export const clientsCloseOverviewDataSchema = z.object({
    workspaces: z.array(closeOverviewWorkspaceRowSchema),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
});
export type ClientsCloseOverviewData = z.infer<
    typeof clientsCloseOverviewDataSchema
>;

export const clientsCloseOverviewResponseSchema = z.union([
    featureDisabledResponseSchema,
    clientsCloseOverviewDataSchema,
]);
export type ClientsCloseOverviewResponse = z.infer<
    typeof clientsCloseOverviewResponseSchema
>;

// ────────────────────────────────────────────────────────────
// Slim endpoint schemas
// ────────────────────────────────────────────────────────────
export const clientsCloseOverviewSlimWorkspaceSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
export type ClientsCloseOverviewSlimWorkspace = z.infer<
    typeof clientsCloseOverviewSlimWorkspaceSchema
>;

export const clientsCloseOverviewSlimQuerySchema =
    clientsCloseOverviewQuerySchema;
export type ClientsCloseOverviewSlimQuery = z.infer<
    typeof clientsCloseOverviewSlimQuerySchema
>;

export const clientsCloseOverviewSlimDataSchema = z.object({
    workspaces: z.array(clientsCloseOverviewSlimWorkspaceSchema),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
});
export type ClientsCloseOverviewSlimData = z.infer<
    typeof clientsCloseOverviewSlimDataSchema
>;

export const clientsCloseOverviewSlimResponseSchema = z.union([
    featureDisabledResponseSchema,
    clientsCloseOverviewSlimDataSchema,
]);
export type ClientsCloseOverviewSlimResponse = z.infer<
    typeof clientsCloseOverviewSlimResponseSchema
>;

// ────────────────────────────────────────────────────────────
// Details endpoint schemas
// ────────────────────────────────────────────────────────────
export const clientsCloseOverviewDetailsQuerySchema = refineFilterQuery(
    filteredQueryBaseSchema.extend({
        workspaceIds: z.array(z.string().uuid()).min(1).max(100),
    }),
);
export type ClientsCloseOverviewDetailsQuery = z.infer<
    typeof clientsCloseOverviewDetailsQuerySchema
>;

export const clientsCloseOverviewDetailsDataSchema = z.object({
    workspaces: z.array(closeOverviewWorkspaceRowSchema),
});
export type ClientsCloseOverviewDetailsData = z.infer<
    typeof clientsCloseOverviewDetailsDataSchema
>;

export const clientsCloseOverviewDetailsResponseSchema = z.union([
    featureDisabledResponseSchema,
    clientsCloseOverviewDetailsDataSchema,
]);
export type ClientsCloseOverviewDetailsResponse = z.infer<
    typeof clientsCloseOverviewDetailsResponseSchema
>;

// ────────────────────────────────────────────────────────────
// Close unread task counts endpoint schemas
// ────────────────────────────────────────────────────────────
export const closeUnreadTaskCountItemSchema = z.object({
    closeId: z.string(),
    unreadTaskCount: z.number().int().nonnegative(),
});
export type CloseUnreadTaskCountItem = z.infer<
    typeof closeUnreadTaskCountItemSchema
>;

export const closeUnreadTaskCountsQuerySchema = z.object({
    organizationId: z.string().uuid(),
    closeIds: z.array(z.string().uuid()).min(1).max(500),
});
export type CloseUnreadTaskCountsQuery = z.infer<
    typeof closeUnreadTaskCountsQuerySchema
>;

export const closeUnreadTaskCountsDataSchema = z.object({
    counts: z.array(closeUnreadTaskCountItemSchema),
});
export type CloseUnreadTaskCountsData = z.infer<
    typeof closeUnreadTaskCountsDataSchema
>;

export const closeUnreadTaskCountsResponseSchema = z.union([
    featureDisabledResponseSchema,
    closeUnreadTaskCountsDataSchema,
]);
export type CloseUnreadTaskCountsResponse = z.infer<
    typeof closeUnreadTaskCountsResponseSchema
>;
